import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import firebase from "firebase/app";
import "firebase/firestore";
import { getPlanDetails, getPlanBadgeColor } from "../../../utils/subscriptionPlans";

import {
    Grid,
    Row,
    Col,
    Button,
    FormGroup,
    FormControl,
    ControlLabel,
    Table,
    Modal
} from "react-bootstrap";
import Card from "components/Card/Card.jsx";

class AdminSubscriptionManagement extends Component {
    constructor(props) {
        super(props);
        this.state = {
            users: [],
            loading: true,
            error: null,
            searchTerm: '',
            showModal: false,
            selectedUser: null,
            newPlan: '',
            newEndDate: '',
            // États pour les filtres
            filterPlan: 'all',
            filterStatus: 'all',
            showFilters: false,
            // États pour la pagination
            currentPage: 1,
            itemsPerPage: 10,
            // États pour la gestion des formations
            trainings: [],
            showTrainingModal: false,
            selectedTraining: null,
            trainingForm: {
                title: '',
                description: '',
                duration: '',
                price: '',
                category: '',
                level: 'beginner',
                isActive: true,
                maxParticipants: '',
                startDate: '',
                endDate: ''
            },
            loadingTrainings: false,
            activeTab: 'users' // 'users' ou 'trainings'
        };
    }

    componentDidMount() {
        this.loadAllUsers();
        this.loadTrainings();
    }

    loadAllUsers = async () => {
        try {
            const db = firebase.firestore();
            const usersSnapshot = await db.collection('users').get();

            const users = [];
            usersSnapshot.forEach(doc => {
                const userData = doc.data();
                users.push({
                    id: doc.id,
                    email: userData.email || 'N/A',
                    displayName: userData.displayName || userData.nom || 'N/A',
                    // Charger toutes les données d'abonnement pour une détection complète
                    subscription: userData.subscription || { plan: 'basic', active: false },
                    plan: userData.plan, // Plan direct
                    subscriptionStatus: userData.subscriptionStatus, // Statut d'abonnement
                    isSubscribed: userData.isSubscribed, // Booléen d'abonnement
                    subscriptionType: userData.subscriptionType, // Type d'abonnement
                    lastLogin: userData.lastLogin,
                    createdAt: userData.createdAt
                });
            });

            this.setState({ users, loading: false });
        } catch (error) {
            this.setState({
                loading: false,
                error: "Erreur lors du chargement des utilisateurs: " + error.message
            });
        }
    }

    formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';

        try {
            const date = timestamp instanceof firebase.firestore.Timestamp
                ? timestamp.toDate()
                : new Date(timestamp);
            return date.toLocaleDateString('fr-FR');
        } catch {
            return 'Date invalide';
        }
    }

    getPlanBadge = (plan) => {
        const planDetails = getPlanDetails(plan);
        const backgroundColor = getPlanBadgeColor(plan);

        return (
            <span
                style={{
                    backgroundColor,
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: '500'
                }}
            >
        {planDetails.name}
      </span>
        );
    }

    handleEditSubscription = (user) => {
        this.setState({
            showModal: true,
            selectedUser: user,
            newPlan: user.subscription.plan || 'basic',
            newEndDate: user.subscription.endDate ? this.formatDateForInput(user.subscription.endDate) : ''
        });
    }

    formatDateForInput = (timestamp) => {
        if (!timestamp) return '';
        try {
            const date = timestamp instanceof firebase.firestore.Timestamp
                ? timestamp.toDate()
                : new Date(timestamp);
            return date.toISOString().split('T')[0];
        } catch {
            return '';
        }
    }

    handleSaveSubscription = async () => {
        const { selectedUser, newPlan, newEndDate } = this.state;

        try {
            const db = firebase.firestore();
            const endDate = newEndDate ? new Date(newEndDate) : null;

            await db.collection('users').doc(selectedUser.id).update({
                'subscription.plan': newPlan,
                'subscription.active': newPlan !== 'basic',
                'subscription.endDate': endDate ? firebase.firestore.Timestamp.fromDate(endDate) : null,
                'subscription.updatedAt': firebase.firestore.FieldValue.serverTimestamp()
            });

            // Mettre à jour l'état local
            this.setState(prevState => ({
                users: prevState.users.map(user =>
                    user.id === selectedUser.id
                        ? {
                            ...user,
                            subscription: {
                                ...user.subscription,
                                plan: newPlan,
                                active: newPlan !== 'basic',
                                endDate: endDate ? firebase.firestore.Timestamp.fromDate(endDate) : null
                            }
                        }
                        : user
                ),
                showModal: false,
                selectedUser: null
            }));

        } catch (error) {
            this.setState({ error: "Erreur lors de la mise à jour: " + error.message });
        }
    }

    // Méthodes pour la gestion des formations
    loadTrainings = async () => {
        try {
            this.setState({ loadingTrainings: true });
            const db = firebase.firestore();
            const trainingsSnapshot = await db.collection('trainings')
                .orderBy('createdAt', 'desc')
                .get();

            const trainings = [];
            trainingsSnapshot.forEach(doc => {
                const trainingData = doc.data();
                trainings.push({
                    id: doc.id,
                    ...trainingData,
                    createdAt: trainingData.createdAt,
                    updatedAt: trainingData.updatedAt
                });
            });

            this.setState({ trainings, loadingTrainings: false });
        } catch (error) {
            this.setState({
                loadingTrainings: false,
                error: "Erreur lors du chargement des formations: " + error.message
            });
        }
    }

    handleCreateTraining = () => {
        this.setState({
            showTrainingModal: true,
            selectedTraining: null,
            trainingForm: {
                title: '',
                description: '',
                duration: '',
                price: '',
                category: '',
                level: 'beginner',
                isActive: true,
                maxParticipants: '',
                startDate: '',
                endDate: ''
            }
        });
    }

    handleEditTraining = (training) => {
        this.setState({
            showTrainingModal: true,
            selectedTraining: training,
            trainingForm: {
                title: training.title || '',
                description: training.description || '',
                duration: training.duration || '',
                price: training.price || '',
                category: training.category || '',
                level: training.level || 'beginner',
                isActive: training.isActive !== false,
                maxParticipants: training.maxParticipants || '',
                startDate: training.startDate || '',
                endDate: training.endDate || ''
            }
        });
    }

    handleTrainingFormChange = (field, value) => {
        this.setState({
            trainingForm: {
                ...this.state.trainingForm,
                [field]: value
            }
        });
    }

    handleSaveTraining = async () => {
        try {
            const { trainingForm, selectedTraining } = this.state;
            const db = firebase.firestore();

            // Validation
            if (!trainingForm.title.trim()) {
                alert('Le titre de la formation est requis');
                return;
            }
            if (!trainingForm.description.trim()) {
                alert('La description de la formation est requise');
                return;
            }

            const trainingData = {
                ...trainingForm,
                price: parseFloat(trainingForm.price) || 0,
                maxParticipants: parseInt(trainingForm.maxParticipants) || null,
                updatedAt: firebase.firestore.FieldValue.serverTimestamp()
            };

            if (selectedTraining) {
                // Mise à jour d'une formation existante
                await db.collection('trainings').doc(selectedTraining.id).update(trainingData);
            } else {
                // Création d'une nouvelle formation
                trainingData.createdAt = firebase.firestore.FieldValue.serverTimestamp();
                await db.collection('trainings').add(trainingData);
            }

            // Recharger les formations et fermer le modal
            await this.loadTrainings();
            this.handleCloseTrainingModal();

        } catch (error) {
            alert('Erreur lors de la sauvegarde: ' + error.message);
        }
    }

    handleDeleteTraining = async (trainingId) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette formation ?')) {
            return;
        }

        try {
            const db = firebase.firestore();
            await db.collection('trainings').doc(trainingId).delete();

            // Recharger les formations
            await this.loadTrainings();

        } catch (error) {
            alert('Erreur lors de la suppression: ' + error.message);
        }
    }

    handleToggleTrainingStatus = async (training) => {
        try {
            const db = firebase.firestore();
            const newStatus = !training.isActive;

            await db.collection('trainings').doc(training.id).update({
                isActive: newStatus,
                updatedAt: firebase.firestore.FieldValue.serverTimestamp()
            });

            // Recharger les formations
            await this.loadTrainings();

        } catch (error) {
            alert('Erreur lors de la mise à jour: ' + error.message);
        }
    }

    // Fonction pour déterminer si un utilisateur a un abonnement actif (même logique que les stats)
    isUserSubscribed = (user) => {
        // Méthode 1: Vérifier subscription.status === 'active'
        if (user.subscription && user.subscription.status === 'active') {
            return { isSubscribed: true, method: 'subscription.status', plan: user.subscription.plan || 'premium' };
        }
        // Méthode 2: Vérifier subscriptionStatus === 'active'
        else if (user.subscriptionStatus === 'active') {
            return { isSubscribed: true, method: 'subscriptionStatus', plan: user.plan || user.subscriptionType || 'premium' };
        }
        // Méthode 3: Vérifier isSubscribed === true
        else if (user.isSubscribed === true) {
            return { isSubscribed: true, method: 'isSubscribed', plan: user.plan || user.subscriptionType || 'premium' };
        }
        // Méthode 4: Vérifier l'existence d'un plan d'abonnement
        else if (user.plan && ['premium', 'annual', 'quarterly', 'mensuel', 'annuel', 'trimestriel'].includes(user.plan)) {
            return { isSubscribed: true, method: 'plan défini', plan: user.plan };
        }
        // Si aucune méthode ne fonctionne, considérer comme inactif/basic
        return { isSubscribed: false, method: 'none', plan: 'basic' };
    }

    filteredUsers = () => {
        const { users, searchTerm, filterPlan, filterStatus } = this.state;

        return users.filter(user => {
            // Filtre par recherche textuelle
            const matchesSearch = !searchTerm ||
                user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.displayName.toLowerCase().includes(searchTerm.toLowerCase());

            // Déterminer le statut d'abonnement de l'utilisateur
            const subscriptionInfo = this.isUserSubscribed(user);
            const isActive = subscriptionInfo.isSubscribed;
            const userPlan = subscriptionInfo.plan;

            // Filtre par plan (utiliser le plan détecté)
            const matchesPlan = filterPlan === 'all' || userPlan === filterPlan;

            // Filtre par statut (utiliser la détection améliorée)
            const matchesStatus = filterStatus === 'all' ||
                (filterStatus === 'active' && isActive) ||
                (filterStatus === 'inactive' && !isActive);

            return matchesSearch && matchesPlan && matchesStatus;
        });
    }

    // Fonction pour obtenir le badge de statut d'un utilisateur
    getUserStatusBadge = (user) => {
        const subscriptionInfo = this.isUserSubscribed(user);
        const isActive = subscriptionInfo.isSubscribed;
        const plan = subscriptionInfo.plan;
        const method = subscriptionInfo.method;

        if (isActive) {
            return {
                text: `✅ Actif (${plan})`,
                color: '#28a745',
                title: `Détecté via: ${method}`
            };
        } else {
            return {
                text: '❌ Inactif',
                color: '#dc3545',
                title: 'Aucun abonnement actif détecté'
            };
        }
    }

    // Gestion des filtres
    handleFilterChange = (filterType, value) => {
        this.setState({
            [filterType]: value
        });
    }

    handleToggleFilters = () => {
        this.setState(prevState => ({
            showFilters: !prevState.showFilters
        }));
    }

    clearAllFilters = () => {
        this.setState({
            searchTerm: '',
            filterPlan: 'all',
            filterStatus: 'all',
            currentPage: 1
        });
    }

    handleCloseTrainingModal = () => {
        this.setState({
            showTrainingModal: false,
            selectedTraining: null,
            trainingForm: {
                title: '',
                description: '',
                duration: '',
                price: '',
                category: '',
                level: 'beginner',
                isActive: true,
                maxParticipants: '',
                startDate: '',
                endDate: ''
            }
        });
    }

    // Fonctions de pagination
    getPaginatedUsers = () => {
        const { currentPage, itemsPerPage } = this.state;
        const filtered = this.filteredUsers();
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filtered.slice(startIndex, endIndex);
    }

    handlePageChange = (page) => {
        this.setState({ currentPage: page });
    }

    handleItemsPerPageChange = (itemsPerPage) => {
        this.setState({
            itemsPerPage: parseInt(itemsPerPage),
            currentPage: 1 // Reset à la première page
        });
    }

    // Reset pagination lors de la recherche ou changement filtres
    handleSearchChange = (searchTerm) => {
        this.setState({
            searchTerm,
            currentPage: 1 // Reset à la première page
        });
    }

    handleFilterChangeWithReset = (filterType, value) => {
        this.setState({
            [filterType]: value,
            currentPage: 1 // Reset à la première page
        });
    }

    render() {
        const {
            loading, error, searchTerm, showModal, selectedUser, newPlan, newEndDate,
            filterPlan, filterStatus, showFilters, users, currentPage, itemsPerPage,
            trainings, showTrainingModal, selectedTraining, trainingForm, loadingTrainings, activeTab
        } = this.state;

        if (loading) {
            return (
                <div className="content">
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <i className="fa fa-spinner fa-spin fa-3x" style={{ color: '#4e73df' }}></i>
                        <h3 style={{ marginTop: '20px' }}>Chargement des utilisateurs...</h3>
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="content">
                    <div className="alert alert-danger">
                        <h4>Erreur</h4>
                        <p>{error}</p>
                        <Button onClick={this.loadAllUsers} style={{ backgroundColor: '#4e73df', color: 'white', border: 'none' }}>
                            Réessayer
                        </Button>
                    </div>
                </div>
            );
        }

        const filteredUsers = this.filteredUsers();
        const paginatedUsers = this.getPaginatedUsers();
        const totalItems = filteredUsers.length;
        const totalPages = Math.ceil(totalItems / itemsPerPage);

        return (
            <div className="content admin-subscriptions-page">
                <Grid fluid>
                    <Row>
                        <Col md={12}>
                            {(
                                <Card
                                    title="Liste des Utilisateurs"
                                    content={
                                        <div className="admin-subscriptions-content">
                                            <div style={{ marginBottom: '20px' }}>
                                                <div className="subscription-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', gap: '20px', marginBottom: '15px' }}>
                                                    <FormGroup style={{ flex: 1 }}>
                                                        <ControlLabel>Rechercher un utilisateur</ControlLabel>
                                                        <FormControl
                                                            type="text"
                                                            placeholder="Email ou nom..."
                                                            value={searchTerm}
                                                            onChange={(e) => this.handleSearchChange(e.target.value)}
                                                        />
                                                    </FormGroup>

                                                    <Button
                                                        onClick={this.handleToggleFilters}
                                                        style={{
                                                            backgroundColor: 'var(--app-primary-color)',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '5px',
                                                            padding: '8px 15px'
                                                        }}
                                                    >
                                                        <i className="fa fa-filter"></i> {showFilters ? 'Masquer' : 'Filtres'}
                                                    </Button>

                                                    <FormGroup style={{ minWidth: '150px' }}>
                                                        <ControlLabel>Éléments par page</ControlLabel>
                                                        <FormControl
                                                            componentClass="select"
                                                            value={itemsPerPage}
                                                            onChange={(e) => this.handleItemsPerPageChange(e.target.value)}
                                                        >
                                                            <option value={5}>5</option>
                                                            <option value={10}>10</option>
                                                            <option value={20}>20</option>
                                                            <option value={50}>50</option>
                                                        </FormControl>
                                                    </FormGroup>
                                                </div>

                                                {/* Panneau de filtres */}
                                                {showFilters && (
                                                    <div style={{
                                                        backgroundColor: '#f8f9fa',
                                                        border: '1px solid #dee2e6',
                                                        borderRadius: '8px',
                                                        padding: '20px',
                                                        marginBottom: '15px'
                                                    }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                                            <h5 style={{ margin: 0, color: 'var(--app-primary-color)' }}>
                                                                <i className="fa fa-filter" style={{ marginRight: '8px' }}></i>
                                                                Filtres de recherche
                                                            </h5>
                                                            <Button
                                                                size="sm"
                                                                onClick={this.clearAllFilters}
                                                                style={{
                                                                    backgroundColor: '#6c757d',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '4px'
                                                                }}
                                                            >
                                                                <i className="fa fa-times"></i> Réinitialiser
                                                            </Button>
                                                        </div>

                                                        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                                                            <FormGroup style={{ minWidth: '200px' }}>
                                                                <ControlLabel>Filtrer par plan</ControlLabel>
                                                                <FormControl
                                                                    componentClass="select"
                                                                    value={filterPlan}
                                                                    onChange={(e) => this.handleFilterChangeWithReset('filterPlan', e.target.value)}
                                                                >
                                                                    <option value="all">Tous les plans</option>
                                                                    <option value="basic">Basique</option>
                                                                    <option value="premium">Mensuel</option>
                                                                    <option value="quarterly">Trimestriel</option>
                                                                    <option value="annual">Annuel</option>
                                                                </FormControl>
                                                            </FormGroup>

                                                            <FormGroup style={{ minWidth: '200px' }}>
                                                                <ControlLabel>Filtrer par statut</ControlLabel>
                                                                <FormControl
                                                                    componentClass="select"
                                                                    value={filterStatus}
                                                                    onChange={(e) => this.handleFilterChangeWithReset('filterStatus', e.target.value)}
                                                                >
                                                                    <option value="all">Tous les statuts</option>
                                                                    <option value="active">✅ Actifs (y compris Basic)</option>
                                                                    <option value="inactive">❌ Inactifs (expirés ou désactivés)</option>
                                                                </FormControl>
                                                            </FormGroup>
                                                        </div>

                                                        {/* Indicateurs de filtres actifs */}
                                                        {(filterPlan !== 'all' || filterStatus !== 'all') && (
                                                            <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
                                                                <small style={{ color: '#6c757d', marginRight: '10px' }}>Filtres actifs:</small>
                                                                {filterPlan !== 'all' && (
                                                                    <span style={{
                                                                        backgroundColor: 'var(--app-primary-color)',
                                                                        color: 'white',
                                                                        padding: '2px 8px',
                                                                        borderRadius: '12px',
                                                                        fontSize: '12px',
                                                                        marginRight: '8px'
                                                                    }}>
                                  Plan: {filterPlan === 'basic' ? 'Basique' :
                                                                        filterPlan === 'premium' ? 'Mensuel' :
                                                                            filterPlan === 'quarterly' ? 'Trimestriel' : 'Annuel'}
                                </span>
                                                                )}
                                                                {filterStatus !== 'all' && (
                                                                    <span style={{
                                                                        backgroundColor: 'var(--app-primary-color)',
                                                                        color: 'white',
                                                                        padding: '2px 8px',
                                                                        borderRadius: '12px',
                                                                        fontSize: '12px',
                                                                        marginRight: '8px'
                                                                    }}>
                                  Statut: {filterStatus === 'active' ? 'Actif' : 'Inactif'}
                                </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            <div style={{ marginBottom: '15px', color: '#6c757d', fontSize: '14px' }}>
                                                Affichage de {((currentPage - 1) * itemsPerPage) + 1} à {Math.min(currentPage * itemsPerPage, totalItems)} sur {totalItems} utilisateurs
                                                {totalItems !== users.length && ` (${users.length} au total)`}
                                            </div>

                                            <div className="subscription-table-wrap" style={{ overflowX: 'auto' }}>
                                                <Table striped bordered hover>
                                                    <thead>
                                                    <tr>
                                                        <th>Utilisateur</th>
                                                        <th>Email</th>
                                                        <th>Plan Actuel</th>
                                                        <th>Statut</th>
                                                        <th>Date de Fin</th>
                                                        <th>Dernière Connexion</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {paginatedUsers.map(user => (
                                                        <tr key={user.id}>
                                                            <td>{user.displayName}</td>
                                                            <td>{user.email}</td>
                                                            <td>{this.getPlanBadge(this.isUserSubscribed(user).plan)}</td>
                                                            <td>
                                                                {(() => {
                                                                    const statusBadge = this.getUserStatusBadge(user);
                                                                    return (
                                                                        <span
                                                                            style={{
                                                                                color: statusBadge.color,
                                                                                fontWeight: '500'
                                                                            }}
                                                                            title={statusBadge.title}
                                                                        >
                                      {statusBadge.text}
                                    </span>
                                                                    );
                                                                })()}
                                                            </td>
                                                            <td>{this.formatDate(user.subscription.endDate)}</td>
                                                            <td>{this.formatDate(user.lastLogin)}</td>
                                                            <td>
                                                                <Button
                                                                    size="sm"
                                                                    style={{ backgroundColor: 'var(--app-primary-color)', color: 'white', border: 'none' }}
                                                                    onClick={() => this.handleEditSubscription(user)}
                                                                >
                                                                    <i className="pe-7s-pen"></i> Modifier
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </Table>
                                            </div>

                                            {/* Contrôles de pagination */}
                                            {totalPages > 1 && (
                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20px', gap: '10px' }}>
                                                    <Button
                                                        disabled={currentPage === 1}
                                                        onClick={() => this.handlePageChange(currentPage - 1)}
                                                        style={{
                                                            backgroundColor: currentPage === 1 ? '#e9ecef' : 'var(--app-primary-color)',
                                                            color: currentPage === 1 ? '#6c757d' : 'white',
                                                            border: 'none',
                                                            borderRadius: '5px',
                                                            padding: '8px 12px'
                                                        }}
                                                    >
                                                        <i className="fa fa-chevron-left"></i> Précédent
                                                    </Button>

                                                    <div style={{ display: 'flex', gap: '5px' }}>
                                                        {[...Array(totalPages)].map((_, index) => {
                                                            const page = index + 1;
                                                            const isCurrentPage = page === currentPage;

                                                            // Logique d'affichage des pages (avec ellipses pour les grandes listes)
                                                            if (totalPages <= 7) {
                                                                // Afficher toutes les pages si <= 7
                                                                return (
                                                                    <Button
                                                                        key={page}
                                                                        onClick={() => this.handlePageChange(page)}
                                                                        style={{
                                                                            backgroundColor: isCurrentPage ? 'var(--app-primary-color)' : '#f8f9fa',
                                                                            color: isCurrentPage ? 'white' : '#495057',
                                                                            border: isCurrentPage ? 'none' : '1px solid #dee2e6',
                                                                            borderRadius: '5px',
                                                                            padding: '8px 12px',
                                                                            minWidth: '40px'
                                                                        }}
                                                                    >
                                                                        {page}
                                                                    </Button>
                                                                );
                                                            } else {
                                                                // Logique avec ellipses pour > 7 pages
                                                                if (page === 1 || page === totalPages ||
                                                                    (page >= currentPage - 1 && page <= currentPage + 1)) {
                                                                    return (
                                                                        <Button
                                                                            key={page}
                                                                            onClick={() => this.handlePageChange(page)}
                                                                            style={{
                                                                                backgroundColor: isCurrentPage ? 'var(--app-primary-color)' : '#f8f9fa',
                                                                                color: isCurrentPage ? 'white' : '#495057',
                                                                                border: isCurrentPage ? 'none' : '1px solid #dee2e6',
                                                                                borderRadius: '5px',
                                                                                padding: '8px 12px',
                                                                                minWidth: '40px'
                                                                            }}
                                                                        >
                                                                            {page}
                                                                        </Button>
                                                                    );
                                                                } else if (page === currentPage - 2 || page === currentPage + 2) {
                                                                    return <span key={page} style={{ padding: '8px 4px' }}>...</span>;
                                                                } else {
                                                                    return null;
                                                                }
                                                            }
                                                        })}
                                                    </div>

                                                    <Button
                                                        disabled={currentPage === totalPages}
                                                        onClick={() => this.handlePageChange(currentPage + 1)}
                                                        style={{
                                                            backgroundColor: currentPage === totalPages ? '#e9ecef' : 'var(--app-primary-color)',
                                                            color: currentPage === totalPages ? '#6c757d' : 'white',
                                                            border: 'none',
                                                            borderRadius: '5px',
                                                            padding: '8px 12px'
                                                        }}
                                                    >
                                                        Suivant <i className="fa fa-chevron-right"></i>
                                                    </Button>
                                                </div>
                                            )}

                                            {/* Message si aucun résultat */}
                                            {totalItems === 0 && (
                                                <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                                                    <i className="fa fa-search fa-3x" style={{ marginBottom: '15px' }}></i>
                                                    <h4>Aucun utilisateur trouvé</h4>
                                                    <p>Essayez de modifier vos critères de recherche ou de filtrage.</p>
                                                </div>
                                            )}


                                        </div>
                                    }
                                />
                            )}

                            {false && (
                                <Card
                                    title="Gestion des Formations"
                                    content={
                                        <div>
                                            {/* Header avec bouton de création */}
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: '20px',
                                                padding: '15px',
                                                backgroundColor: '#f8f9fa',
                                                borderRadius: '8px'
                                            }}>
                                                <div>
                                                    <h4 style={{ margin: 0, color: '#495057' }}>
                                                        <i className="fa fa-graduation-cap" style={{ marginRight: '10px', color: 'var(--app-primary-color)' }}></i>
                                                        Formations Disponibles
                                                    </h4>
                                                    <p style={{ margin: '5px 0 0 0', color: '#6c757d', fontSize: '14px' }}>
                                                        {trainings.length} formation{trainings.length > 1 ? 's' : ''} au total
                                                    </p>
                                                </div>
                                                <Button
                                                    style={{ backgroundColor: 'var(--app-primary-color)', border: 'none', borderRadius: '25px', padding: '10px 20px' }}
                                                    onClick={this.handleCreateTraining}
                                                >
                                                    <i className="fa fa-plus" style={{ marginRight: '8px' }}></i>
                                                    Nouvelle Formation
                                                </Button>
                                            </div>

                                            {/* Liste des formations */}
                                            {loadingTrainings ? (
                                                <div style={{ textAlign: 'center', padding: '50px' }}>
                                                    <i className="fa fa-spinner fa-spin fa-2x" style={{ color: 'var(--app-primary-color)' }}></i>
                                                    <p style={{ marginTop: '15px', color: '#6c757d' }}>Chargement des formations...</p>
                                                </div>
                                            ) : trainings.length === 0 ? (
                                                <div style={{
                                                    textAlign: 'center',
                                                    padding: '50px',
                                                    backgroundColor: '#f8f9fa',
                                                    borderRadius: '8px',
                                                    border: '2px dashed #dee2e6'
                                                }}>
                                                    <i className="fa fa-graduation-cap fa-3x" style={{ color: '#dee2e6', marginBottom: '20px' }}></i>
                                                    <h4 style={{ color: '#6c757d', marginBottom: '10px' }}>Aucune formation créée</h4>
                                                    <p style={{ color: '#6c757d', marginBottom: '20px' }}>
                                                        Commencez par créer votre première formation
                                                    </p>
                                                    <Button
                                                        style={{ backgroundColor: 'var(--app-primary-color)', border: 'none', borderRadius: '25px' }}
                                                        onClick={this.handleCreateTraining}
                                                    >
                                                        <i className="fa fa-plus" style={{ marginRight: '8px' }}></i>
                                                        Créer une Formation
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'grid', gap: '20px' }}>
                                                    {trainings.map(training => (
                                                        <div
                                                            key={training.id}
                                                            style={{
                                                                backgroundColor: 'white',
                                                                border: '1px solid #dee2e6',
                                                                borderRadius: '12px',
                                                                padding: '20px',
                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                                transition: 'all 0.3s ease'
                                                            }}
                                                        >
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                                                <div style={{ flex: 1 }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                                                                        <h5 style={{ margin: 0, color: '#495057', marginRight: '15px' }}>
                                                                            {training.title}
                                                                        </h5>
                                                                        <span style={{
                                                                            padding: '4px 12px',
                                                                            borderRadius: '15px',
                                                                            fontSize: '12px',
                                                                            fontWeight: '500',
                                                                            backgroundColor: training.isActive ? '#d4edda' : '#f8d7da',
                                                                            color: training.isActive ? '#155724' : '#721c24'
                                                                        }}>
                                      {training.isActive ? '✅ Active' : '❌ Inactive'}
                                    </span>
                                                                    </div>

                                                                    <p style={{ color: '#6c757d', marginBottom: '15px', lineHeight: '1.5' }}>
                                                                        {training.description}
                                                                    </p>

                                                                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                                                                        {training.category && (
                                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                                <i className="fa fa-tag" style={{ color: 'var(--app-primary-color)', marginRight: '5px' }}></i>
                                                                                <span style={{ fontSize: '14px', color: '#495057' }}>{training.category}</span>
                                                                            </div>
                                                                        )}
                                                                        {training.duration && (
                                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                                <i className="fa fa-clock-o" style={{ color: 'var(--app-primary-color)', marginRight: '5px' }}></i>
                                                                                <span style={{ fontSize: '14px', color: '#495057' }}>{training.duration}</span>
                                                                            </div>
                                                                        )}
                                                                        {training.price && (
                                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                                <i className="fa fa-money" style={{ color: 'var(--app-primary-color)', marginRight: '5px' }}></i>
                                                                                <span style={{ fontSize: '14px', color: '#495057', fontWeight: '500' }}>{training.price} F CFA</span>
                                                                            </div>
                                                                        )}
                                                                        {training.maxParticipants && (
                                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                                <i className="fa fa-users" style={{ color: 'var(--app-primary-color)', marginRight: '5px' }}></i>
                                                                                <span style={{ fontSize: '14px', color: '#495057' }}>Max: {training.maxParticipants}</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                <div style={{ display: 'flex', gap: '10px', marginLeft: '20px' }}>
                                                                    <Button
                                                                        size="sm"
                                                                        style={{
                                                                            backgroundColor: training.isActive ? '#ffc107' : '#28a745',
                                                                            border: 'none',
                                                                            borderRadius: '20px',
                                                                            padding: '5px 15px'
                                                                        }}
                                                                        onClick={() => this.handleToggleTrainingStatus(training)}
                                                                        title={training.isActive ? 'Désactiver' : 'Activer'}
                                                                    >
                                                                        <i className={`fa fa-${training.isActive ? 'pause' : 'play'}`}></i>
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        style={{ backgroundColor: 'var(--app-primary-color)', border: 'none', borderRadius: '20px', padding: '5px 15px' }}
                                                                        onClick={() => this.handleEditTraining(training)}
                                                                    >
                                                                        <i className="fa fa-edit"></i>
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        style={{ backgroundColor: '#dc3545', border: 'none', borderRadius: '20px', padding: '5px 15px' }}
                                                                        onClick={() => this.handleDeleteTraining(training.id)}
                                                                    >
                                                                        <i className="fa fa-trash"></i>
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    }
                                />
                            )}
                        </Col>
                    </Row>
                </Grid>

                <style>{`
                    .admin-subscriptions-page {
                        min-height: 100vh;
                        padding: 24px;
                        background: #f6f9fa;
                    }
                    .admin-subscriptions-page .card {
                        overflow: hidden;
                        border: 1px solid #e3ebee;
                        border-radius: 13px;
                        box-shadow: 0 7px 22px rgba(31, 58, 69, 0.05);
                    }
                    .admin-subscriptions-page .card .header {
                        color: #263746;
                    }
                    .admin-subscriptions-content .subscription-toolbar {
                        align-items: flex-end !important;
                        padding: 16px;
                        background: #f8fbfc;
                        border: 1px solid #e5edef;
                        border-radius: 11px;
                    }
                    .admin-subscriptions-content .form-control {
                        min-height: 38px;
                        border: 1px solid #d7e1e5;
                        border-radius: 8px;
                        box-shadow: none;
                    }
                    .admin-subscriptions-content .form-control:focus {
                        border-color: var(--app-primary-color);
                        box-shadow: 0 0 0 3px rgba(var(--app-primary-rgb), 0.14);
                    }
                    .admin-subscriptions-content .subscription-table-wrap {
                        border: 1px solid #e3ebee;
                        border-radius: 10px;
                    }
                    .admin-subscriptions-content table {
                        margin-bottom: 0;
                        font-size: 13px;
                    }
                    .admin-subscriptions-content table > thead > tr > th {
                        padding: 12px 10px;
                        color: #405563;
                        background: #f3f8f9;
                        border-bottom: 1px solid #e3ebee;
                        font-size: 11px;
                        text-transform: uppercase;
                        white-space: nowrap;
                    }
                    .admin-subscriptions-content table > tbody > tr > td {
                        padding: 11px 10px;
                        color: #475569;
                        vertical-align: middle;
                    }
                    .admin-subscriptions-page button:focus-visible {
                        outline: 3px solid rgba(var(--app-primary-rgb), 0.28);
                        outline-offset: 2px;
                    }
                    @media (max-width: 767px) {
                        .admin-subscriptions-page { padding: 14px; }
                        .admin-subscriptions-content .subscription-toolbar {
                            align-items: stretch !important;
                            flex-direction: column;
                            gap: 10px !important;
                        }
                    }
                `}</style>

                {/* Modal de modification */}
                <Modal show={showModal} onHide={() => this.setState({ showModal: false })}>
                    <Modal.Header closeButton>
                        <Modal.Title>Modifier l'abonnement</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {selectedUser && (
                            <div>
                                <h5>Utilisateur: {selectedUser.displayName}</h5>
                                <p>Email: {selectedUser.email}</p>

                                <FormGroup>
                                    <ControlLabel>Plan d'abonnement</ControlLabel>
                                    <FormControl
                                        componentClass="select"
                                        value={newPlan}
                                        onChange={(e) => this.setState({ newPlan: e.target.value })}
                                    >
                                        <option value="basic">Basique</option>
                                        <option value="premium">Mensuel</option>
                                        <option value="quarterly">Trimestriel</option>
                                        <option value="annual">Annuel</option>
                                    </FormControl>
                                </FormGroup>

                                <FormGroup>
                                    <ControlLabel>Date de fin</ControlLabel>
                                    <FormControl
                                        type="date"
                                        value={newEndDate}
                                        onChange={(e) => this.setState({ newEndDate: e.target.value })}
                                    />
                                </FormGroup>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onClick={() => this.setState({ showModal: false })}>
                            Annuler
                        </Button>
                        <Button
                            style={{ backgroundColor: 'var(--app-primary-color)', color: 'white', border: 'none' }}
                            onClick={this.handleSaveSubscription}
                        >
                            Sauvegarder
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal pour créer/éditer une formation */}
                <Modal show={showTrainingModal} onHide={this.handleCloseTrainingModal} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <i className="fa fa-graduation-cap" style={{ marginRight: '10px', color: 'var(--app-primary-color)' }}></i>
                            {selectedTraining ? 'Modifier la formation' : 'Nouvelle formation'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <ControlLabel>Titre de la formation *</ControlLabel>
                                    <FormControl
                                        type="text"
                                        placeholder="Ex: Formation React avancé"
                                        value={trainingForm.title}
                                        onChange={(e) => this.handleTrainingFormChange('title', e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <ControlLabel>Catégorie</ControlLabel>
                                    <FormControl
                                        type="text"
                                        placeholder="Ex: Développement Web"
                                        value={trainingForm.category}
                                        onChange={(e) => this.handleTrainingFormChange('category', e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <FormGroup>
                            <ControlLabel>Description *</ControlLabel>
                            <FormControl
                                componentClass="textarea"
                                rows="4"
                                placeholder="Décrivez le contenu et les objectifs de la formation..."
                                value={trainingForm.description}
                                onChange={(e) => this.handleTrainingFormChange('description', e.target.value)}
                            />
                        </FormGroup>

                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <ControlLabel>Durée</ControlLabel>
                                    <FormControl
                                        type="text"
                                        placeholder="Ex: 3 jours, 20h"
                                        value={trainingForm.duration}
                                        onChange={(e) => this.handleTrainingFormChange('duration', e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <ControlLabel>Prix (F CFA)</ControlLabel>
                                    <FormControl
                                        type="number"
                                        placeholder="0"
                                        value={trainingForm.price}
                                        onChange={(e) => this.handleTrainingFormChange('price', e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <ControlLabel>Participants max</ControlLabel>
                                    <FormControl
                                        type="number"
                                        placeholder="Ex: 20"
                                        value={trainingForm.maxParticipants}
                                        onChange={(e) => this.handleTrainingFormChange('maxParticipants', e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <ControlLabel>Niveau</ControlLabel>
                                    <FormControl
                                        componentClass="select"
                                        value={trainingForm.level}
                                        onChange={(e) => this.handleTrainingFormChange('level', e.target.value)}
                                    >
                                        <option value="beginner">Débutant</option>
                                        <option value="intermediate">Intermédiaire</option>
                                        <option value="advanced">Avancé</option>
                                        <option value="expert">Expert</option>
                                    </FormControl>
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <ControlLabel>Date de début</ControlLabel>
                                    <FormControl
                                        type="date"
                                        value={trainingForm.startDate}
                                        onChange={(e) => this.handleTrainingFormChange('startDate', e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <ControlLabel>Date de fin</ControlLabel>

                                    <FormControl
                                        type="date"
                                        value={trainingForm.endDate}
                                        onChange={(e) => this.handleTrainingFormChange('endDate', e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <FormGroup>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <input
                                    type="checkbox"
                                    id="isActive"
                                    checked={trainingForm.isActive}
                                    onChange={(e) => this.handleTrainingFormChange('isActive', e.target.checked)}
                                    style={{ marginRight: '8px' }}
                                />
                                <label htmlFor="isActive" style={{ margin: 0, cursor: 'pointer' }}>
                                    Formation active (visible pour les utilisateurs)
                                </label>
                            </div>
                        </FormGroup>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onClick={this.handleCloseTrainingModal}>
                            Annuler
                        </Button>
                        <Button
                            style={{ backgroundColor: 'var(--app-primary-color)', border: 'none' }}
                            onClick={this.handleSaveTraining}
                        >
                            <i className="fa fa-save" style={{ marginRight: '8px' }}></i>
                            {selectedTraining ? 'Mettre à jour' : 'Créer'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        );
    }
}

AdminSubscriptionManagement.propTypes = {
    profile: PropTypes.object
};

const mapStateToProps = state => ({
    profile: state.user.profile
});

export default connect(mapStateToProps)(AdminSubscriptionManagement);